import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AxiosError } from "axios";

import { IGiveawayAuto, ITicketsAuto } from "../../../interfaces";

import { EmitError } from "../../../utils/EmitError";
import Loading from "../../../components/Loading";
import GiveawaysAutoService from "../../../services/http/services/GiveawaysAutoService";
import { isSameMonth } from "date-fns";
import TicketsAutoService from "../../../services/http/services/TicketsAutoService";
import TicketsAutoList from "../../../components/TicketsAutoList ";

const MONTHS = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
]

export default function GiveawayTicketsAuto() {
    const [giveawayAuto, setGiveawayAuto] = useState<IGiveawayAuto>();
    const [ticketsAuto, setTicketsAuto] = useState<Array<ITicketsAuto>>([]);
    const [totalTickets, setTotalTickets] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingTickets, setIsLoadingTickets] = useState<boolean>(false);

    const { id } = useParams();

    const navigate = useNavigate();

    const giveawayBeginAndEndAtSameMonth = giveawayAuto && isSameMonth(new Date(giveawayAuto.period_beginning), new Date(giveawayAuto.period_ending));

    const listTitle: string | undefined = useMemo(() => {
        if (!giveawayAuto) {
            return undefined;
        }

        const dateBeginning = new Date(giveawayAuto.period_beginning);

        if (giveawayBeginAndEndAtSameMonth) {
            return `${giveawayAuto.description} - ${MONTHS[dateBeginning.getMonth()]}/${dateBeginning.getFullYear()}`;
        }

        const dateEnding = new Date(giveawayAuto.period_ending);

        return `${giveawayAuto.description} - ${MONTHS[dateBeginning.getMonth()]}/${dateBeginning.getFullYear()} à ${MONTHS[dateEnding.getMonth()]}/${dateEnding.getFullYear()}`;
    }, [giveawayAuto, giveawayBeginAndEndAtSameMonth]);

    const loadMore = useCallback(async () => {
        if (isLoadingTickets) {
            return;
        }

        const controller = new AbortController();

        try {
            setIsLoadingTickets(true);

            const ticketsData = await TicketsAutoService.listTicketsByGiveawayId(Number(id), page, controller.signal);

            if (ticketsData.records.length === 0) {
                setHasMore(false);
            }

            setTicketsAuto((prevState) => [...prevState, ...ticketsData.records]);
            setPage((prevState) => prevState + 1);
            setIsLoadingTickets(false);

        } catch (err) {
            if (err instanceof AxiosError && err.name === "CanceledError") {
                return;
            }

            setIsLoadingTickets(false);
            EmitError(err);
        }

        return () => {
            controller.abort();
        }
    }, [id, page, isLoadingTickets]);

    useEffect(() => {
        if (!id) {
            return;
        }

        const controller = new AbortController();

        async function loadData() {
            try {
                setIsLoading(true);

                const [giveawayData, ticketsData] = await Promise.all([
                    GiveawaysAutoService.getGiveawayById({id: Number(id), signal: controller.signal}),
                    TicketsAutoService.listTicketsByGiveawayId(Number(id), 1, controller.signal)
                ]);

                setGiveawayAuto(giveawayData);
                setTicketsAuto(ticketsData.records);
                if (ticketsData.records.length < 100) {
                    setHasMore(false);
                }
                setTotalTickets(ticketsData?.count || 0);
                setPage((prevState) => prevState + 1);
                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'giveawayTickets_asodkaskldasd');
                setIsLoading(false);
            }
        }

        loadData();

        return () => {
            controller.abort();
        }
    }, [id, navigate]);

    const handleClickGoBack = useCallback(() => {
        navigate('/sorteios/auto');
    }, [navigate]);

    return (
        <Loading loading={isLoading} >
            <TicketsAutoList
                paginated
                tickets={ticketsAuto}
                handleClickGoBack={handleClickGoBack}
                title={listTitle}
                loadMore={loadMore}
                hasMore={hasMore}
                isLoading={isLoading}
                emptyListMessage="Não há bilhetes concorrendo neste sorteio"
                totalTickets={totalTickets}
            />
        </Loading >
    )
}
